import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function fmtDate(ts: any): string | null {
  const ms = tsToMs(ts);
  if (!ms) return null;
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDaysLeft(ts: any): string | null {
  const ms = tsToMs(ts);
  if (!ms) return null;
  const d = Math.ceil((ms - Date.now()) / (24 * 60 * 60 * 1000));
  if (d < 0) return 'Ended';
  if (d === 0) return 'Today';
  return `${d}d`;
}

interface CandidateRow {
  uid: string;
  name: string;
  email: string;
  enrollmentStatus: 'pending' | 'approved' | null;
  engagementStatus: 'active' | 'placed' | 'paused' | null;
  lastActivityMs: number;
  nextSessionDate: any;
  programEndDate: any;
}

export function CoachCaseload() {
  const { coach } = useAuth();
  const navigate = useNavigate();
  const coachId = coach!.coachId;

  const [rows, setRows] = useState<CandidateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [engagementFilter, setEngagementFilter] = useState('');
  const [quickFilter, setQuickFilter] = useState<'' | 'inactive14' | 'pending'>('');
  const [declining, setDeclining] = useState<Set<string>>(new Set());

  const isIndependent = coach?.coach.coachType === 'independent';
  const [firmName, setFirmName] = useState(coach?.coach.firmName ?? '');
  const [firmNameEditing, setFirmNameEditing] = useState(false);
  const [firmNameDraft, setFirmNameDraft] = useState('');
  const [savingFirmName, setSavingFirmName] = useState(false);
  const [firmNameError, setFirmNameError] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => { load(); }, [coachId]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const snap = await getDocs(
        query(collection(db, 'profiles'), where('coachId', '==', coachId))
      );
      const data: CandidateRow[] = snap.docs.map(d => {
        const p = d.data();
        const lastActivityMs = tsToMs(p.lastJobActivityAt) || tsToMs(p.lastActiveAt);
        return {
          uid: d.id,
          name: p.manual_name ?? p.name ?? p.displayName ?? '',
          email: p.authEmail ?? '',
          enrollmentStatus: p.enrollmentStatus ?? null,
          engagementStatus: p.engagementStatus ?? null,
          lastActivityMs,
          nextSessionDate: p.nextSessionDate ?? null,
          programEndDate: p.programEndDate ?? null,
        };
      });
      data.sort((a, b) => a.lastActivityMs - b.lastActivityMs);
      setRows(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load caseload.');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async (uid: string, name: string) => {
    if (!window.confirm(`Decline the pending enrollment for ${name || 'this candidate'}? This cannot be undone.`)) return;
    setDeclining(prev => new Set(prev).add(uid));
    try {
      await httpsCallable(getFunctions(), 'coachDeclinePendingEnrollment')({ candidateUid: uid });
      setRows(prev => prev.filter(r => r.uid !== uid));
    } catch (e: any) {
      setError(e.message || 'Failed to decline enrollment. Please try again.');
    } finally {
      setDeclining(prev => { const s = new Set(prev); s.delete(uid); return s; });
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setInviting(true);
    setInviteResult(null);
    try {
      await httpsCallable(getFunctions(), 'inviteCandidate')({ candidateEmail: email });
      setInviteResult({ type: 'success', message: `Invite sent to ${email}.` });
      setInviteEmail('');
    } catch (e: any) {
      setInviteResult({ type: 'error', message: e?.message || 'Something went wrong. Please try again.' });
    } finally {
      setInviting(false);
    }
  };

  const saveFirmName = async () => {
    setSavingFirmName(true);
    setFirmNameError('');
    try {
      const val = firmNameDraft.trim() || null;
      await updateDoc(doc(db, 'coaches', coachId), { firmName: val });
      setFirmName(val ?? '');
      setFirmNameEditing(false);
    } catch (e: any) {
      setFirmNameError(e.message || 'Failed to save.');
    } finally {
      setSavingFirmName(false);
    }
  };

  const INACTIVE_DAYS = 14;
  const daysSince = (ms: number) => ms ? Math.floor((Date.now() - ms) / 86400000) : null;

  const inactive14Count = rows.filter(r => {
    if (r.enrollmentStatus !== 'approved') return false;
    const d = daysSince(r.lastActivityMs);
    return d === null || d >= INACTIVE_DAYS;
  }).length;
  const pendingCount = rows.filter(r => r.enrollmentStatus === 'pending').length;

  const q = search.trim().toLowerCase();
  let filtered = engagementFilter
    ? rows.filter(r => (r.engagementStatus ?? 'active') === engagementFilter)
    : rows;
  if (quickFilter === 'inactive14') {
    filtered = filtered.filter(r => {
      if (r.enrollmentStatus !== 'approved') return false;
      const d = daysSince(r.lastActivityMs);
      return d === null || d >= INACTIVE_DAYS;
    });
  } else if (quickFilter === 'pending') {
    filtered = filtered.filter(r => r.enrollmentStatus === 'pending');
  }
  if (q) filtered = filtered.filter(r => r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q));

  return (
    <Layout>
      <div style={{ maxWidth: 960 }}>
        <div className="page-header" style={{ marginBottom: 24 }}>
          <div>
            <h1 className="page-title">My Caseload</h1>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
              {coach?.coach.name}
            </div>
            {isIndependent && (
              firmNameEditing ? (
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    className="form-input"
                    value={firmNameDraft}
                    onChange={e => { setFirmNameDraft(e.target.value); setFirmNameError(''); }}
                    placeholder="Firm / company name (optional)"
                    style={{ maxWidth: 260, margin: 0, fontSize: 13 }}
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') saveFirmName();
                      if (e.key === 'Escape') { setFirmNameEditing(false); setFirmNameDraft(firmName); }
                    }}
                  />
                  <button
                    className="btn-primary"
                    style={{ padding: '6px 14px', fontSize: 12 }}
                    onClick={saveFirmName}
                    disabled={savingFirmName}
                  >
                    {savingFirmName ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    className="btn-secondary"
                    style={{ padding: '6px 12px', fontSize: 12 }}
                    onClick={() => { setFirmNameEditing(false); setFirmNameDraft(firmName); }}
                  >
                    Cancel
                  </button>
                  {firmNameError && (
                    <span style={{ fontSize: 12, color: 'var(--warning)', width: '100%' }}>{firmNameError}</span>
                  )}
                </div>
              ) : (
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, color: firmName ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
                    {firmName || 'No firm name'}
                  </span>
                  <button
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', padding: 0 }}
                    onClick={() => { setFirmNameDraft(firmName); setFirmNameEditing(true); }}
                  >
                    Edit
                  </button>
                </div>
              )
            )}
          </div>
          <button
            className="btn-secondary"
            onClick={load}
            disabled={loading}
            style={{ padding: '6px 16px', fontSize: 13 }}
          >
            Refresh
          </button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

        {!loading && rows.length > 0 && (
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
            <span>{rows.length} candidate{rows.length !== 1 ? 's' : ''}</span>
            {inactive14Count > 0 && (
              <button
                onClick={() => setQuickFilter(f => f === 'inactive14' ? '' : 'inactive14')}
                style={{
                  background: 'none', border: 'none', borderBottom: quickFilter === 'inactive14' ? '1px solid var(--text-primary)' : '1px solid transparent',
                  padding: '0 0 1px', cursor: 'pointer', fontSize: 13,
                  color: quickFilter === 'inactive14' ? 'var(--text-primary)' : 'var(--text-secondary)',
                  fontWeight: quickFilter === 'inactive14' ? 600 : 400,
                }}
              >
                {inactive14Count} inactive {INACTIVE_DAYS}+ days
              </button>
            )}
            {pendingCount > 0 && (
              <button
                onClick={() => setQuickFilter(f => f === 'pending' ? '' : 'pending')}
                style={{
                  background: 'none', border: 'none', borderBottom: quickFilter === 'pending' ? '1px solid var(--text-primary)' : '1px solid transparent',
                  padding: '0 0 1px', cursor: 'pointer', fontSize: 13,
                  color: quickFilter === 'pending' ? 'var(--text-primary)' : 'var(--text-secondary)',
                  fontWeight: quickFilter === 'pending' ? 600 : 400,
                }}
              >
                {pendingCount} pending approval
              </button>
            )}
          </div>
        )}

        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, alignItems: 'center' }}>
            <input
              className="form-input"
              type="search"
              placeholder="Search by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: 320, margin: 0 }}
            />
            <select
              className="form-input"
              value={engagementFilter}
              onChange={e => setEngagementFilter(e.target.value)}
              style={{ maxWidth: 180, margin: 0 }}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="placed">Placed</option>
              <option value="paused">Paused</option>
            </select>
          </div>

          {loading ? (
            <div className="loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
              {q ? `No results for "${search}".` : 'No candidates assigned yet.'}
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Status</th>
                    <th>Engagement</th>
                    <th>Last Job Activity</th>
                    <th>Next Session</th>
                    <th>Days Left</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => {
                    const days = r.lastActivityMs
                      ? Math.floor((Date.now() - r.lastActivityMs) / (24 * 60 * 60 * 1000))
                      : null;
                    const isPending = r.enrollmentStatus === 'pending';
                    return (
                      <tr
                        key={r.uid}
                        style={{ cursor: isPending ? 'default' : 'pointer' }}
                        onClick={isPending ? undefined : () => navigate(`/coach/candidate/${r.uid}`)}
                      >
                        <td>
                          <div style={{ fontWeight: 500 }}>{r.name || '—'}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, fontFamily: 'monospace' }}>
                            {r.email || '—'}
                          </div>
                        </td>
                        <td>
                          {isPending ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <StatusBadge status={r.enrollmentStatus} />
                              <button
                                className="btn-danger"
                                style={{ padding: '3px 10px', fontSize: 12 }}
                                onClick={e => { e.stopPropagation(); handleDecline(r.uid, r.name); }}
                                disabled={declining.has(r.uid)}
                              >
                                {declining.has(r.uid) ? 'Declining…' : 'Decline'}
                              </button>
                            </div>
                          ) : (
                            <StatusBadge status={r.enrollmentStatus} />
                          )}
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                          {(() => {
                            const s = r.engagementStatus ?? 'active';
                            return s.charAt(0).toUpperCase() + s.slice(1);
                          })()}
                        </td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                          {days === null ? '—' : days === 0 ? 'Today' : `${days}d ago`}
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                          {fmtDate(r.nextSessionDate) ?? ''}
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                          {fmtDaysLeft(r.programEndDate) ?? ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
            {filtered.length} candidate{filtered.length !== 1 ? 's' : ''}{q ? ' matched' : ''}
          </div>
        )}

        {isIndependent && (
          <div style={{ marginTop: 28 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>
              Invite a Candidate
            </div>
            <div className="card" style={{ padding: '16px 20px' }}>
              <form onSubmit={handleInvite} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  className="form-input"
                  type="email"
                  placeholder="Candidate email"
                  value={inviteEmail}
                  onChange={e => { setInviteEmail(e.target.value); setInviteResult(null); }}
                  disabled={inviting}
                  style={{ maxWidth: 280, margin: 0, fontSize: 13 }}
                />
                <button
                  className="btn-primary"
                  type="submit"
                  disabled={inviting || !inviteEmail.trim()}
                  style={{ padding: '8px 18px', fontSize: 13 }}
                >
                  {inviting ? 'Sending…' : 'Send Invite'}
                </button>
              </form>
              {inviteResult && (
                <div style={{ marginTop: 10, fontSize: 13, color: inviteResult.type === 'success' ? 'var(--accent-text)' : 'var(--error)' }}>
                  {inviteResult.message}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatusBadge({ status }: { status: 'pending' | 'approved' | null }) {
  if (status === 'approved') return <span className="badge badge-active">Approved</span>;
  if (status === 'pending') {
    return (
      <span className="badge" style={{ background: 'var(--warning-dim)', color: 'var(--warning)' }}>
        Pending
      </span>
    );
  }
  return <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>;
}
